'use client';

import type { EmblaOptionsType } from 'embla-carousel';
import type { AutoplayOptionsType } from 'embla-carousel-autoplay';
import { useEffect, useMemo, useRef } from 'react';
import { EmblaArrows } from './components/EmblaArrows';
import { EmblaPagination } from './components/EmblaPagination';
import { getSlideClasses } from './helpers';
import { useInitEmblaCarousel } from './hooks/useInitEmblaCarousel';
import { ScEmblaCarousel } from './styled';

interface IEmblaCarouselProps<T> {
  slides: T[];
  slide: React.ComponentType<T>;
  slidesPerView?: number;
  spaceBetween?: number;
  options?: Pick<
    EmblaOptionsType,
    'loop' | 'align' | 'containScroll' | 'startIndex'
  >;
  autoplay?: Partial<AutoplayOptionsType>;
  pagination?: boolean;
  compactPagination?: boolean;
  arrows?: boolean;
  // Groups every `slidesPerView` items into a single Embla slide, so drag,
  // arrows and pagination all snap by whole pages instead of one item at a time.
  groupSlides?: boolean;
  // Fires with the index (in `slides`) of the last item in the newly
  // selected slide, on every scroll after the initial one.
  onSlideChange?: (lastItemIndex: number) => void;
}

const groupItems = <T,>(items: T[], groupSize: number): T[][] => {
  if (groupSize <= 1) return items.map((item) => [item]);

  const groups: T[][] = [];
  for (let i = 0; i < items.length; i += groupSize) {
    groups.push(items.slice(i, i + groupSize));
  }
  return groups;
};

export function EmblaCarousel<T extends object>({
  slides,
  slide: Slide,
  slidesPerView,
  spaceBetween = 16,
  options,
  autoplay,
  pagination,
  compactPagination,
  arrows,
  groupSlides,
  onSlideChange,
}: IEmblaCarouselProps<T>) {
  const groupSize =
    groupSlides && slidesPerView != null
      ? Math.max(1, Math.floor(slidesPerView))
      : 1;

  // startIndex is expressed in slide (item) terms by callers, but grouped
  // slides collapse `groupSize` items into a single Embla slide, so it needs
  // converting to a group index before reaching Embla.
  const emblaOptions = useMemo(() => {
    if (options?.startIndex == null || groupSize <= 1) return options;
    return {
      ...options,
      startIndex: Math.floor(options.startIndex / groupSize),
    };
  }, [options, groupSize]);

  const {
    emblaRef,
    selectedIndex,
    canScrollPrev,
    canScrollNext,
    scrollPrev,
    scrollNext,
    scrollTo,
  } = useInitEmblaCarousel({
    options: emblaOptions,
    autoplay,
  });

  const slideGroups = useMemo(
    () => groupItems(slides, groupSize),
    [slides, groupSize]
  );

  const total = slideGroups.length;

  // Ref, not a dep: `slideGroups` gets a new identity on every re-render,
  // which must not re-fire onSlideChange - only a real selectedIndex change should.
  const slideGroupsRef = useRef(slideGroups);
  slideGroupsRef.current = slideGroups;

  const isFirstSelect = useRef(true);
  useEffect(() => {
    if (isFirstSelect.current) {
      isFirstSelect.current = false;
      return;
    }
    const currentGroup = slideGroupsRef.current[selectedIndex];
    if (!currentGroup) return;
    onSlideChange?.(selectedIndex * groupSize + currentGroup.length - 1);
  }, [selectedIndex, groupSize, onSlideChange]);

  return (
    <ScEmblaCarousel
      $align={options?.align ?? 'start'}
      $grouped={groupSize > 1}
      $reservePaginationSpace={Boolean(
        arrows && pagination && !compactPagination
      )}
      $slidesPerView={slidesPerView}
      $spaceBetween={spaceBetween}
    >
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slideGroups.map((group, key) => (
            <div
              className={getSlideClasses(key, selectedIndex, total)}
              key={`slide-${key}`}
            >
              {groupSize > 1 ? (
                <div
                  className="embla__group"
                  style={{
                    gap: `${spaceBetween}px`,
                    padding: `0 ${spaceBetween}px`,
                  }}
                >
                  {group.map((props, itemIndex) => (
                    <Slide {...props} key={itemIndex} />
                  ))}
                </div>
              ) : (
                // groupSize <= 1 here, and groupItems wraps each item in its
                // own single-element array in that case, so group[0] always exists.
                <Slide {...group[0]} />
              )}
            </div>
          ))}
        </div>
      </div>

      {arrows && (
        <EmblaArrows
          canScrollNext={canScrollNext}
          canScrollPrev={canScrollPrev}
          onNext={scrollNext}
          onPrev={scrollPrev}
        />
      )}

      {pagination && (
        <EmblaPagination
          compact={compactPagination}
          count={total}
          onSelect={scrollTo}
          selectedIndex={selectedIndex}
        />
      )}
    </ScEmblaCarousel>
  );
}
